window.theme = window.theme || {};

if (!window.theme.stones) {

  const API_URL = 'https://app.allurez.com/api/legacy-db';

  class StonesAPI {

    /**
     * Shapes that have a generic 360° sequence on the legacy CDN, mapped to the
     * last frame number available for each. Verified against
     * images.allurez.com — shapes absent from this map (trilliant, baguette, …)
     * simply get no spinner. The assets are static, so the counts are hardcoded
     * rather than probed at runtime.
     *
     * @static {Object<string, number>}
     */
    static GENERIC_360_FRAMES = {
      round: 374,
      princess: 374,
      emerald: 374,
      asscher: 373,
      cushion: 374,
      marquise: 374,
      oval: 374,
      radiant: 374,
      pear: 374,
      heart: 374
    };

    /**
     * Fetch stones api to get a stone data by id
     * 
     * @param {number} id - The id of the stone
     * @param {string} type - The type of the stone (gemstone, diamond or moissanite)
     * @returns {Promise<Object>} The stone data
     */
    async getStoneById(id, type = 'gemstone') {

      let result = null;

      // Check if the data is in session storage
      if (window.sessionStorage.getItem(`stone-${id}`)) {
        try {
          result = JSON.parse(window.sessionStorage.getItem(`stone-${id}`));
        } catch (error) {
          console.error('Error parsing stone data from session storage:', error);
          window.sessionStorage.removeItem(`stone-${id}`);
        }
      }

      // If the data is not in session storage, fetch it from the API
      if (result === null) {

        // TODO: Modify the api to have only the stones endpoint
        const apiUrl = type === 'gemstone' ? `${API_URL}/stones/gemstones/get/${id}` : 
                       type === 'moissanite' ? `${API_URL}/stones/moissanites/get/${id}` : 
                       type === 'color-diamond' ? `${API_URL}/stones/color-diamonds/get/${id}` : 
                       `${API_URL}/gems/${id}`;
        const response = await fetch(apiUrl);
        const { data } = await response.json();

        if (!data) {
          console.error('Error fetching stone data from the API');
          return;
        }

        // Data transformation
        data.id = data.gems_item_id || id;
        data.title = this.getStoneTitle(data, type);
        data.description = this.getStoneDescription(data, type);
        data.price = this.getPrice(data, type);
        data.price_compare_at = this.getCompareAtPrice(data, type);
        data.image = this.getStoneImage(data, type);
        data.shape = this.getShapeName(data.shape_id);
        data.type = this.getStoneType(data);
  
        // Store the data in session storage
        window.sessionStorage.setItem(`stone-${id}`, JSON.stringify(data));

        result = data;
      }

      return result;
    }

    /**
     * Get the estimated retail price for comparison
     * 
     * @param {number} price - The price
     * @param {boolean} labGrown - Whether the diamond is lab grown
     * @returns {string} The estimated retail price for comparison
     */
    getCompareAtPrice(item, type = 'gemstone') {

      const markup = (type === 'gemstone' || type === 'diamond' || type === 'color-diamond') ? 2.5 : 2.5;
      const price = this.getPrice(item, type);

      return price * markup;
    }

    /**
     * Get the price
     * 
     * @param {object} item - The stone item
     * @param {string} type - The type of the item (gemstone, diamond, color-diamond or moissanite)
     * @returns {string} The price
     */
    getPrice(item, type = 'gemstone') {

      return type === 'gemstone' ? item.price_carat*100 : 
             type === 'moissanite' ? item.total_price*100 : 
             item.total_price*100;
    }

    /**
     * Get the estimated retail price
     * 
     * @param {object} item - The stone item
     * @param {string} type - The type of the stone (gemstone, diamond, color-diamond or moissanite)
     * @returns {string} The estimated retail price
     */
    getEstimatedRetailPrice(item, type = 'gemstone') {

      if (type === 'diamond' || type === 'color-diamond') {

        const price = this.getPrice(item, type);

        return price * 2.5;

      }

      return null;
    }

    /**
     * Get the shape name
     * 
     * @param {number} shapeId - The shape ID
     * @returns {string} The shape name
     */
    getShapeName(shapeId) {
      const shapes = {
        1: 'Princess',
        2: 'Cushion',
        3: 'Oval',
        4: 'Pear',
        5: 'Round',
        6: 'Marquise',
        7: 'Emerald',
        8: 'Radiant',
        9: 'Asscher',
        10: 'Heart'
      };
      return shapes[shapeId] || '';
    }

    /**
     * Resolve the 360° frame sequence to show for a diamond.
     *
     * Mirrors the branching in the legacy platform's diamond_details.php:
     * a real per-stone sequence wins; otherwise a generic spin for the shape,
     * but only when the stone has no third-party video and isn't a colored
     * diamond. See docs/diamond-360-viewer.md.
     *
     * Returns `video` when a ready-made MP4 exists for the shape, and always
     * returns the frame-sequence fields so the frame player can serve as the
     * fallback. `poster` is the first frame, used as the video's poster image.
     *
     * @param {object} item - The stone item, as returned by getStoneById
     * @returns {{base: string, sequence: string, frames: number, poster: string, video: string|null}|null}
     */
    getStone360(item) {

      if (!item) {
        return null;
      }

      // The real per-stone sets live on a different host to the generic ones.
      // No MP4s exist for these, so they always play through the frame player.
      if (Number(item.is_360_video) === 1 && Number(item.video_360_cnt) > 0 && item.seller_no && item.sku) {
        return this.describe360(
          'https://magicvideos.allurez.com/gemsimages/magicvideos',
          `${item.seller_no}/${item.sku}`,
          Number(item.video_360_cnt),
          null
        );
      }

      // A third-party video of the actual stone is always better than a stock spin.
      if (item.video_url || item.v360_url) {
        return null;
      }

      // Generic sequences are white diamonds — wrong for fancy colors.
      if (item.is_colored_diamond) {
        return null;
      }

      const shape = (item.shape || this.getShapeName(item.shape_id) || '').toLowerCase();
      const frames = StonesAPI.GENERIC_360_FRAMES[shape];

      if (!frames) {
        return null;
      }

      // The shape → video map lives in video-360.js, the single source of
      // truth shared with the moissanite PDP. Absent if that script didn't load,
      // in which case the frame player still serves the sequence.
      return this.describe360(
        'https://images.allurez.com/gemsimages/magicvideos',
        shape,
        frames,
        window.Video360 ? window.Video360.srcForShape(shape) : null
      );
    }

    /**
     * Assemble a 360 descriptor, deriving the poster from the first frame.
     *
     * @param {string} base - CDN root for the frame sequence
     * @param {string} sequence - Path segment identifying the sequence
     * @param {number} frames - Last frame number in the sequence
     * @param {string|null} video - Ready-made MP4, when one exists
     * @returns {{base: string, sequence: string, frames: number, poster: string, video: string|null}}
     */
    describe360(base, sequence, frames, video) {

      // The sequence path may be nested ("NVD/ABC123"); the file prefix is the last segment.
      const prefix = sequence.split('/').pop();

      return {
        base,
        sequence,
        frames,
        poster: `${base}/${sequence}/${prefix}_01.jpg`,
        video
      };
    }

    /**
     * Get stone summary details
     * 
     * @param {object} item - The stone item
     * @param {string} type - The type of the stone (gemstone, diamond, color-diamond or moissanite)
     * @returns 
     */
    getStoneSummaryDetails(item, type = 'gemstone') {
      
      if (type === 'gemstone') {
        return `Cut Grade: ${item.gem_grade} | Origin: ${item.lab_grown ? 'Lab Created' : 'Natural'}`;
      }

      if (type === 'moissanite') {
        return `Cut: ${item.cut} | Type: ${item.moissanite_type}`;
      }

      // For both diamond and color-diamond
      return `Cut: ${item.cut} | Color: ${item.color} | Clarity: ${item.clarity}`;
    }

    /**
     * Get the stone title
     * 
     * @param {object} item - The stone item
     * @param {string} type - The type of the stone (gemstone, diamond, color-diamond or moissanite)
     * @returns {string} The stone title
     */
    getStoneTitle(item, type = 'gemstone') {

      if (type === 'gemstone') {
        return `${this.getShapeName(item.shape_id)} shape ${item.lab_grown ? 'Lab Created' : 'Natural'} ${item.gem_stone_type} ${item.stone_type} ${item.gem_size} (${item.carat} ct)`;
      }

      if (type === 'moissanite') {
        return `${item.carat} carat ${this.getShapeName(item.shape_id)} ${item.moissanite_type} ${item.stone_type} ${item.color} color`;
      }

      if (type === 'color-diamond') {
        const labGrown = item.lab_grown ? 'Lab Grown' : 'Natural ';
        return `${labGrown} ${item.carat} carat ${item.intensity} ${item.color}-${item.clarity} ${this.getShapeName(item.shape_id)} Diamond`;
      }

      const labGrown = item.lab_grown == '1' ? 'Lab Grown ' : 'Natural ';

      // Regular diamond
      return `${labGrown}${item.carat} carat ${item.color}-${item.clarity} ${this.getShapeName(item.shape_id)} Diamond`;
    }

    /**
     * Get the stone description
     * 
     * @param {object} item - The gemstone item
     * @param {string} type - The type of the item (gemstone, diamond, color-diamond or moissanite)
     * @returns {string} The gemstone description
     */
    getStoneDescription(item, type = 'gemstone') {

      const shapeName = this.getShapeName(item.shape_id);

      if (type === 'gemstone') {

        return `This ${item.gem_stone_type} of approximately ${item.carat}ct has been hand selected by our GIA-certified gemologist ensuring premium quality and rarity. This gemstone also has an approximate measurement of ${item.gem_size}, is a ${shapeName} shape, ${item.color} color, and ${item.gem_grade} grade.`;
      }

      if (type === 'moissanite') {
        return `This loose ${item.measurement} Colorless ${shapeName} Shape Moissanite has been hand selected by our GIA-certified gemologists. It has an approximate carat weight of ${item.carat} and is an Allurez exclusive ${item.moissanite_type}, giving the stone a ${item.color} color, ensuring flawless brilliance and sparkle. Allurez's moissanites are the finest moissanites in the world.`;
      }

      if (type === 'color-diamond') {
        return `This certified Brilliant-Cut ${shapeName} shape loose color diamond is of ${item.carat}-carat, ${item.cut}-cut, ${item.color} color, and ${item.clarity} clarity. This natural color diamond is accompanied by a diamond grading report from ${item.report}, and a full 30 day inspection period.`;
      }

      // Regular diamond
      return `This certified Brilliant-Cut ${shapeName} shape loose diamond is of ${item.carat}-carat, ${item.cut}-cut, ${item.color} color, and ${item.clarity} clarity. This natural diamond is accompanied by a diamond grading report from ${item.report}, and a full 30 day inspection period.`;
    }

    /**
     * Get the stone image
     * 
     * @param {object} item - The stone item
     * @param {string} type - The type of the item (gemstone, diamond, color-diamond or moissanite)
     * @returns {string} The stone image
     */
    getStoneImage(item, type = 'gemstone') {

      const shapeName = this.getShapeName(item.shape_id);

      if (type === 'gemstone') {
        return  item.image_url ? item.image_url : `https://images.allurez.com/gemstoneimages/large/${shapeName.toLowerCase().replace(' ', '-')}-${item.gem_stone_type.toLowerCase().replace(' ', '-')}.jpg`;
      }

      if (type === 'moissanite') {
        return item.image_url ? item.image_url : `https://images.allurez.com/gemsimages/diamond_shapes/diamond/2018/${shapeName}/${shapeName.toLowerCase().replace(' ', '-')}.gif`;
      }

      if (type === 'color-diamond') {

        return item.image_url ? item.image_url : `https://images.allurez.com/gemsimages/fancy_diamond/large/${item.color.toLowerCase().replace(' ', '-')}_${shapeName.toLowerCase().replace(' ', '-')}.png`;
      }

      // For both diamond and color-diamond
      if (item.image_url !== '' && item.image_url !== null) {
        return item.image_url;
      }

      return `https://images.allurez.com/gemsimages/diamond_shapes/diamond/2018/${shapeName}/${shapeName.toLowerCase().replace(' ', '-')}.gif`;
    }

    /**
     * Get the stone type
     * 
     * @param {object} item - The stone item
     * @returns {string} The stone type
     */
    getStoneType(item) {
      return item.stone_type.toLowerCase();
    }

    /**
     * Normalise a raw legacy-DB field into a displayable string.
     *
     * Rows imported from supplier feeds sometimes carry the *string* "undefined"
     * (or "null"/"N/A") instead of an empty column, so a plain truthiness check
     * lets those leak into the page. An all-zero measurement is likewise a stand-in
     * for "not recorded". Everything unusable collapses to ''.
     *
     * @param {*} value - The raw field value
     * @returns {string} The cleaned value, or '' when there is nothing to show
     */
    cleanValue(value) {

      if (value === undefined || value === null) {
        return '';
      }

      const str = String(value).trim();

      if (['', 'undefined', 'null', 'n/a', '-'].includes(str.toLowerCase())) {
        return '';
      }

      return /^0+(\.0+)?$/.test(str) ? '' : str;
    }

    /**
     * Build a girdle range from its min/max columns, dropping whichever side is
     * missing. Returns '' when neither side is usable, so callers can hide the row.
     *
     * @param {*} min - The girdle_min field
     * @param {*} max - The girdle_max field
     * @param {string} separator - Text placed between the two bounds
     * @returns {string} The formatted girdle range
     */
    formatGirdle(min, max, separator = ' to ') {

      const bounds = [this.cleanValue(min), this.cleanValue(max)].filter(Boolean);

      // A range with identical bounds reads better as a single value.
      if (bounds.length === 2 && bounds[0] === bounds[1]) {
        return bounds[0];
      }

      return bounds.join(separator);
    }

    /**
     * Generate an SEO-friendly slug for a stone
     *
     * @param {object} item - The stone item
     * @param {string} type - The type of the stone (gemstone, diamond, color-diamond, moissanite)
     * @returns {string} The SEO slug
     */
    getStoneSlug(item, type = 'gemstone') {
      const title = this.getStoneTitle(item, type);
      const slug = title
        .toLowerCase()
        .replace(/\./g, '-')
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      return `${slug}-sid-${item.id}`;
    }

    /**
     * Extract the numeric ID from an SEO slug (segment after `sid-` marker)
     *
     * @param {string} slug - The SEO slug
     * @returns {string} The numeric ID
     */
    getIdFromSlug(slug) {
      if (!slug) return '';
      const match = slug.match(/(?:^|-)sid-(\d+)(?:-|$)/);
      return match ? match[1] : '';
    }

    /**
     * Get filter options for stones
     * 
     * @param {string} type - The type of stone (gemstone, diamond, moissanite, color-diamond)
     * @returns {Promise<Object>} The filter options data
     */
    async getFilterOptions(type = 'gemstone') {
      // Handle different API endpoints for different stone types
      const apiUrl = type === 'gemstone' ? `${API_URL}/stones/gemstones/filters` : 
                     type === 'moissanite' ? `${API_URL}/stones/moissanites/filters` : 
                     type === 'color-diamond' ? `${API_URL}/stones/color-diamonds/filters` : 
                     `${API_URL}/gems-filter-options`;
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    }

    /**
     * Get stones with filters and sorting
     * 
     * @param {string} type - The type of stone (gemstone, diamond, moissanite, color-diamond)
     * @param {Object} params - The query parameters
     * @param {number} params.page - The page number (default: 1)
     * @param {number} params.per_page - Items per page (default: 60)
     * @param {Object} params.filters - Filter parameters
     * @param {string} params.sort_by - Sort field (price or carat)
     * @param {string} params.sort_order - Sort order (asc or desc)
     * @returns {Promise<Object>} The filtered stones data
     */
    async getFilteredStones(type = 'gemstone', params = {}) {
      // Handle different API endpoints for different stone types
      const baseUrl = type === 'gemstone' ? `${API_URL}/stones/gemstones` : 
                      type === 'moissanite' ? `${API_URL}/stones/moissanites` : 
                      type === 'color-diamond' ? `${API_URL}/stones/color-diamonds` : 
                      `${API_URL}/gems`;
      
      const url = new URL(baseUrl);
      
      // Add pagination params
      url.searchParams.set('page', params.page || 1);
      url.searchParams.set('per_page', params.per_page || 60);
      
      // Add sorting params
      if (params.sort_by) {
        url.searchParams.set('sort_by', params.sort_by);
      }
      if (params.sort_order) {
        url.searchParams.set('sort_order', params.sort_order);
      }
      
      // Add filter params
      if (params.filters) {
        Object.keys(params.filters).forEach(key => {
          const value = params.filters[key];
          if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => url.searchParams.append(`${key}[]`, v));
            } else {
              url.searchParams.set(key, value);
            }
          }
        });
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    }

    /**
     * Add to cart
     * 
     * @param {object} item - The item
     * @param {string} type - The type of the item (gemstone, diamond or moissanite)
     * @returns {Promise<Object>} The result of the add to cart
     */
    async addToCart(id, type = 'gemstone') {

      if (!id) {
        console.error('addToCart id is required');
        return;
      }

      // Get the item from the API
      const item = await this.getStoneById(id, type);

      // Get the product from the API using the item type (gemstone, diamond or moissanite)
      const product = await theme.product.loadProduct(item.type);

      // If the product is not found, return
      if (!product) {
        console.error('addToCart product not found');
        return;
      }

      const properties = {
        _stone: true,
        _stone_type: type,
        _stone_shape: item.shape,
        _stone_price: item.price,
        _stone_carat: item.carat,
        _stone_image: item.image,
        _stone_title: item.title,
        _stone_color: item.color,
        _stone_sku: item.sku,
        _stone_id: item.id,
        _stone_slug: this.getStoneSlug(item, type),
        style: item.sku
      };

      const addIf = (key, value) => {
        const str = this.cleanValue(value);
        if (str === '') return;
        properties[key] = str;
      };

      // Certificate
      addIf('_stone_certificate', item.report);
      addIf('_stone_certificate_no', item.certificate_no);
      addIf('_stone_certificate_image', item.certificate_image);

      // Grading (diamond / color-diamond / moissanite)
      addIf('_stone_clarity', item.clarity);
      addIf('_stone_cut', item.cut);
      addIf('_stone_polish', item.polish);
      addIf('_stone_symmetry', item.symmetry);
      addIf('_stone_fluorescence', item.fluorescence);

      // Measurements
      addIf('_stone_measurement', item.measurement);
      addIf('_stone_lw_ratio', item.lw_ratio);
      addIf('_stone_table', item.table_percent);
      addIf('_stone_depth_pct', item.depth);
      addIf('_stone_girdle', this.formatGirdle(item.girdle_min, item.girdle_max, ' – '));
      addIf('_stone_culet', item.culet);

      // Lab grown
      if (item.lab_grown !== undefined && item.lab_grown !== null && item.lab_grown !== '') {
        const labGrownStr = String(item.lab_grown);
        const isLab = labGrownStr === '1' || labGrownStr.toLowerCase() === 'yes' || labGrownStr.toLowerCase() === 'true';
        properties['_stone_lab_grown'] = isLab ? 'Yes' : 'No';
      }

      // Color diamond specific
      addIf('_stone_intensity', item.intensity);
      addIf('_stone_overtone', item.overtone);

      // Gemstone specific
      addIf('_stone_gem_type', item.gem_stone_type);
      addIf('_stone_gem_grade', item.gem_grade);
      addIf('_stone_gem_enhancement', item.gem_enhancement);
      addIf('_stone_gem_brilliance', item.gem_brilliance);
      addIf('_stone_origin', item.gem_origin);
      addIf('_stone_gem_size', item.gem_size);

      // Moissanite specific
      addIf('_stone_moissanite_type', item.moissanite_type);

      // Source / seller
      addIf('_stone_source', item.item_source);
      addIf('_stone_seller', item.seller);
      addIf('_stone_seller_code', item.seller_code);
      addIf('_stone_seller_no', item.seller_no);
      addIf('_stone_country', item.country);
      addIf('_stone_org_sku', item.org_sku);

      // Video
      addIf('_stone_video', item.v360_url || item.video_url);

      const result = await window.theme.cart.add(product.variants[0].id, 1, properties, true);

      return result;
    }
  };

  window.theme.stones = new StonesAPI();

}